import { useForm } from "@/src/provider/Form/FormProvider";
import Group from "@/src/components/Wizard/Group";
import CheckIcon from "@/src/assets/icons/common/toggleable/IconCheck";
import CrossIcon from "@/src/assets/icons/common/toggleable/IconClose";
import { emailDefault, isPhoneNumberDefault, requiredValidator } from "@/src/services/validationRules";
import wizardStyle from "@/src/components/Form/wizardStyle";
import FormLegend from "@/src/components/Form/Common/FormLegend/FormLegend";
import ValidatorSelectField from "@/src/components/FormFields/ValidatorSelectField";
import MenuItem from "@mui/material/MenuItem";
import { Child } from "@/src/types/Entities/Child";
import Person from "@/src/components/Form/Common/Person/Person";
import Grid from "@mui/material/Grid";
import React, { useEffect } from "react";
import { translate } from "@/src/services/translations/translations";
import { AbstractCase } from "@/src/types/Entities/AbstractCase";
import Address from "@/src/components/Form/Common/Address/Address";
import ValidatorTextField from "@/src/components/FormFields/ValidatorTextField";

type WitnessDataProps = {
  product: AbstractCase;
};

export default function WitnessData({ product }: WitnessDataProps) {
  const { values, handleChange } = useForm();

  useEffect(() => {
    if (!values.witnessesToBeRemoved) {
      values.witnessesToBeRemoved = [];
    }

    handleChange({
      target: {
        name: "witnessCount",
        value: values.witnesses.length.toString(),
        type: "text",
      },
    });
  }, []);

  useEffect(() => {
    const witnessCount = parseInt(values.witnessCount, 10);

    if (values.witnesses.length < witnessCount) {
      for (let i = values.witnesses.length; i < witnessCount; i++) {
        handleChange({
          target: {
            name: `witnesses.${i}`,
            value: { gender: "", givenName: "", familyName: "", id: `new-${i}` },
            type: "text",
          },
        });
      }
    }

    if (values.witnesses.length > witnessCount) {
      let updated = [];

      for (let i = 0; i < values.witnesses.length; i++) {
        if (i < witnessCount) {
          updated.push(values.witnesses[i]);
        } else {
          if (!(typeof values.witnesses[i].id === "string" && values.witnesses[i].id.startsWith("new-"))) {
            values.witnessesToBeRemoved.push(values.witnesses[i].id);
          }
        }
      }

      handleChange({
        target: {
          name: `witnesses`,
          value: updated,
          type: "text",
        },
      });
    }
  }, [values.witnessCount]);

  useEffect(() => {
    if (values.hasWitness === "no") {
      handleChange({
        target: {
          name: "witnessCount",
          value: null,
          type: "text",
        },
      });

      for (let i = 0; i < values.witnesses.length; i++) {
        if (!(typeof values.witnesses[i].id === "string" && values.witnesses[i].id.startsWith("new-"))) {
          values.witnessesToBeRemoved.push(values.witnesses[i].id);
        }
      }

      handleChange({
        target: {
          name: "witnesses",
          value: [],
          type: "text",
        },
      });
    } else {
      handleChange({
        target: {
          name: "witnessCount",
          value: values.witnesses.length > 0 ? values.witnesses.length.toString() : "1",
          type: "text",
        },
      });
    }
  }, [values.hasWitness]);

  return (
    <>
      <Grid size={12}>
        <Group
          type="radio"
          name="hasWitness"
          question={translate(`${product.name}.hasWitness.question`, values)}
          options={[
            {
              labelText: translate(`${product.name}.hasWitness.options.yes.label`, values),
              labelIcon: <CheckIcon />,
              labelIconActive: <CheckIcon active />,
              value: "yes",
            },
            {
              labelText: translate(`${product.name}.hasWitness.options.no.label`, values),
              labelIcon: <CrossIcon />,
              labelIconActive: <CrossIcon active />,
              value: "no",
            },
          ]}
          validators={[requiredValidator("Bitte wählen Sie eine der angezeigten Antworten aus")]}
        />
      </Grid>
      {values.hasWitness === "yes" && (
        <>
          <Grid sx={wizardStyle.gridTopSpaced} size={12}>
            <FormLegend>{translate(`${product.name}.witnessCount.question`, values)}</FormLegend>
          </Grid>
          <Grid size={12}>
            <ValidatorSelectField
              label={translate(`${product.name}.witnessCount.label`, values)}
              name="witnessCount"
              validators={[requiredValidator("Bitte geben Sie an, wie viele Zeugen es gibt.")]}
            >
              <MenuItem value="1">1</MenuItem>
              <MenuItem value="2">2</MenuItem>
              <MenuItem value="3">3</MenuItem>
              <MenuItem value="4">4</MenuItem>
              <MenuItem value="5">5</MenuItem>
            </ValidatorSelectField>
          </Grid>
        </>
      )}
      {values.hasWitness === "yes" &&
        parseInt(values.witnessCount, 10) > 0 &&
        values.witnesses.map((child: Child, index: number) => (
          <React.Fragment key={child.id}>
            <Person
              path={`witnesses.${index}`}
              headline={`Bitte machen Sie hier weitere Angaben zu der ${index + 1}. Person, die Sie als Zeugen angeben möchten.`}
            />
            <Address path={`witnesses.${index}.residenceAddress`} headline="" fieldsAreRequired={false} />
            <Grid size={12}>
              <ValidatorTextField name={`witnesses.${index}.email`} label="E-Mail-Adresse" autoComplete="email" validators={[emailDefault]} />
            </Grid>
            <Grid size={12}>
              <ValidatorTextField name={`witnesses.${index}.telephone`} label="Telefonnummer" autoComplete="tel" validators={isPhoneNumberDefault} />
            </Grid>
          </React.Fragment>
        ))}
    </>
  );
}
